/**
 * Resend Email Service  
 * 
 * Handles all email sending operations using Resend API
 * Includes transactional emails and marketing campaigns
 */

import { supabase } from './supabaseClient';
import emailTemplates from '../utils/emailTemplates';
import type { Usuario, Aviso } from '../types';
import { Logger } from '../utils/logger';

// Resend API configuration
const RESEND_API_URL = '/api/send-email';

interface SendEmailParams {
    to: string | string[];
    subject: string;
    html: string;
    text?: string;
    from?: string;
    cc?: string | string[];
    bcc?: string | string[];
    attachments?: { filename: string; content?: string; path?: string }[];
}

interface EmailResponse {
    success: boolean;
    id?: string;
    error?: string;
}

/**
 * Send email via proxy endpoint (Vite dev proxy or Cloudflare Worker in production)
 */
export const sendEmail = async (params: SendEmailParams): Promise<EmailResponse> => {
    try {
        Logger.debug('Sending email via proxy endpoint');

        const response = await fetch(RESEND_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Internal-Secret': import.meta.env.VITE_INTERNAL_API_SECRET || ''
            },
            body: JSON.stringify({
                from: params.from || 'PublicaloGratis <noreply@publicalogratis.com>',
                to: Array.isArray(params.to) ? params.to : [params.to],
                subject: params.subject,
                html: params.html,
                text: params.text,
                cc: params.cc,
                bcc: params.bcc,
                attachments: params.attachments
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || data.error || 'Failed to send email');
        }

        Logger.debug('Email sent successfully');
        return {
            success: true,
            id: data.id
        };
    } catch (error: any) {
        Logger.error('Error sending email', error);
        return {
            success: false,
            error: error.message || 'Failed to send email'
        };
    }
};

/**
 * Validate email address
 */
export const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// ============================================
// Transactional Emails
// ============================================

/**
 * Send welcome email to new user
 */
export const sendWelcomeEmail = async (user: Usuario): Promise<EmailResponse> => {
    if (!user.email || !validateEmail(user.email)) {
        return { success: false, error: 'Invalid email address' };
    }

    const html = emailTemplates.welcome({
        userName: user.nombre
    });

    return sendEmail({
        to: user.email,
        subject: '¡Bienvenido a PublicaloGratis! 🎉',
        html
    });
};

/**
 * Send aviso approved notification
 */
export const sendAvisoApprovedEmail = async (user: Usuario, aviso: Aviso): Promise<EmailResponse> => {
    if (!user.email || !validateEmail(user.email)) {
        return { success: false, error: 'Invalid email address' };
    }

    const avisoLink = `${window.location.origin}/aviso/${aviso.id_aviso}`;

    const html = emailTemplates.avisoApproved({
        userName: user.nombre,
        avisoTitulo: aviso.titulo,
        avisoLink
    });

    return sendEmail({
        to: user.email,
        subject: `✅ Tu aviso "${aviso.titulo}" fue aprobado`,
        html
    });
};

/**
 * Send aviso rejected notification
 */
export const sendAvisoRejectedEmail = async (
    user: Usuario,
    aviso: Aviso,
    razon?: string
): Promise<EmailResponse> => {
    if (!user.email || !validateEmail(user.email)) {
        return { success: false, error: 'Invalid email address' };
    }

    const html = emailTemplates.avisoRejected({
        userName: user.nombre,
        avisoTitulo: aviso.titulo,
        razon
    });

    return sendEmail({
        to: user.email,
        subject: `Aviso "${aviso.titulo}" - Requiere revisión`,
        html
    });
};

/**
 * Send payment confirmation email
 */
export const sendPaymentConfirmationEmail = async (
    user: Usuario,
    monto: number,
    concepto: string
): Promise<EmailResponse> => {
    if (!user.email || !validateEmail(user.email)) {
        return { success: false, error: 'Invalid email address' };
    }

    const html = emailTemplates.paymentConfirmation({
        userName: user.nombre,
        monto,
        concepto
    });

    return sendEmail({
        to: user.email,
        subject: `💰 Pago confirmado - $${monto.toFixed(2)}`,
        html
    });
};

/**
 * Send password reset email
 */
export const sendPasswordResetEmail = async (
    user: Usuario,
    resetLink: string
): Promise<EmailResponse> => {
    if (!user.email || !validateEmail(user.email)) {
        return { success: false, error: 'Invalid email address' };
    }

    const html = emailTemplates.passwordReset({
        userName: user.nombre,
        resetLink
    });

    return sendEmail({
        to: user.email,
        subject: '🔐 Restablecer tu contraseña',
        html
    });
};

// ============================================
// Campaign Management
// ============================================

interface CampaignData {
    id_campana?: number;
    titulo: string;
    asunto: string;
    contenido: string;
    audiencia_tipo: 'todos' | 'verificados' | 'filtro' | 'empresas' | 'manual';
    audiencia_filtros?: any;
    fecha_programada?: string;
    archivo_url?: string;
    archivo_nombre?: string;
}

/**
 * Create email campaign
 */
export const createCampaign = async (
    campaignData: CampaignData,
    createdBy: number
): Promise<{ success: boolean; campaignId?: number; error?: string }> => {
    try {
        const { data, error } = await supabase
            .from('campanas_email')
            .insert({
                titulo: campaignData.titulo,
                asunto: campaignData.asunto,
                contenido: campaignData.contenido,
                audiencia_tipo: campaignData.audiencia_tipo,
                audiencia_filtros: campaignData.audiencia_filtros,
                fecha_programada: campaignData.fecha_programada,
                archivo_url: campaignData.archivo_url,
                archivo_nombre: campaignData.archivo_nombre,
                estado: 'borrador',
                creado_por: createdBy,
                fecha_creacion: new Date().toISOString()
            })
            .select()
            .maybeSingle();

        if (error) throw error;

        return {
            success: true,
            campaignId: data?.id_campana
        };
    } catch (error: any) {
        Logger.error('Error creating campaign', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get recipients for a campaign based on audience type
 */
const getCampaignRecipients = async (
    audienciaTipo: string,
    audienciaFiltros?: any
): Promise<Usuario[]> => {
    try {
        let query = supabase
            .from('usuarios')
            .select('id_usuario, nombre, email')
            .not('email', 'is', null);

        // Filter by audience type
        if (audienciaTipo === 'verificados') {
            query = query.eq('verificado', true);
        } else if (audienciaTipo === 'empresas') {
            // Filter for company clients
            query = query
                .eq('rol', 'cliente_empresa') // Assuming 'cliente_empresa' is the string value
                .eq('habilita_tienda', true);
        } else if (audienciaTipo === 'manual' && audienciaFiltros?.ids) {
            // Manual selection by ID list
            if (Array.isArray(audienciaFiltros.ids) && audienciaFiltros.ids.length > 0) {
                query = query.in('id_usuario', audienciaFiltros.ids);
            } else {
                return []; // No IDs provided
            }
        } else if (audienciaTipo === 'filtro' && audienciaFiltros) {
            // Apply custom filters
            if (audienciaFiltros.rol) {
                query = query.eq('rol', audienciaFiltros.rol);
            }
            if (audienciaFiltros.provincia) {
                query = query.eq('provincia', audienciaFiltros.provincia);
            }
            if (audienciaFiltros.fecha_desde) {
                query = query.gte('fecha_registro', audienciaFiltros.fecha_desde);
            }
        }

        const { data, error } = await query;

        if (error) throw error;
        return (data || []) as Usuario[];
    } catch (error) {
        Logger.error('Error getting recipients', error);
        return [];
    }
};

/**
 * Send email campaign
 */
export const sendCampaign = async (campaignId: number): Promise<{
    success: boolean;
    sent: number;
    failed: number;
    error?: string;
}> => {
    try {
        // Get campaign details
        const { data: campaign, error: campaignError } = await supabase
            .from('campanas_email')
            .select('*')
            .eq('id_campana', campaignId)
            .maybeSingle();

        if (campaignError || !campaign) {
            throw new Error('Campaign not found');
        }

        // Update campaign status to "enviando"
        await supabase
            .from('campanas_email')
            .update({ estado: 'enviando' })
            .eq('id_campana', campaignId);

        // Get recipients
        const recipients = await getCampaignRecipients(
            campaign.audiencia_tipo,
            campaign.audiencia_filtros
        );

        if (recipients.length === 0) {
            throw new Error('No recipients found for this campaign');
        }

        // Update total recipients
        await supabase
            .from('campanas_email')
            .update({ destinatarios_total: recipients.length })
            .eq('id_campana', campaignId);

        // Send emails in batches
        let sent = 0;
        let failed = 0;
        const BATCH_SIZE = 10; // Send 10 emails at a time

        for (let i = 0; i < recipients.length; i += BATCH_SIZE) {
            const batch = recipients.slice(i, i + BATCH_SIZE);

            await Promise.all(batch.map(async (recipient) => {
                try {
                    // Create personalized content
                    // Create personalized content with flexible regex (case insensitive, allows spaces)
                    const personalizedContent = campaign.contenido
                        .replace(/\{\{\s*nombre\s*\}\}/gi, recipient.nombre || 'Usuario')
                        .replace(/\{\{\s*email\s*\}\}/gi, recipient.email);

                    const html = emailTemplates.newsletter({
                        content: personalizedContent,
                        unsubscribeLink: `${window.location.origin}/unsubscribe?email=${recipient.email}`
                    });

                    const emailParams: any = {
                        to: recipient.email,
                        subject: campaign.asunto,
                        html
                    };

                    // Add attachment if exists
                    if (campaign.archivo_url) {
                        emailParams.attachments = [{
                            filename: campaign.archivo_nombre || 'archivo_adjunto',
                            path: campaign.archivo_url
                        }];
                    }

                    const result = await sendEmail(emailParams);

                    // Track individual send
                    await supabase.from('email_envios').insert({
                        id_campana: campaignId,
                        id_usuario: recipient.id_usuario,
                        email: recipient.email,
                        estado: result.success ? 'enviado' : 'fallido',
                        error_mensaje: result.error,
                        external_id: result.id,
                        fecha_envio: new Date().toISOString()
                    });

                    if (result.success) {
                        sent++;
                    } else {
                        failed++;
                    }
                } catch (error) {
                    Logger.error(`Error sending to recipient`, error);
                    failed++;
                }
            }));

            // Small delay between batches to avoid rate limiting
            if (i + BATCH_SIZE < recipients.length) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }

        // Update campaign statistics
        await supabase
            .from('campanas_email')
            .update({
                estado: 'enviada',
                emails_enviados: sent,
                emails_fallidos: failed,
                fecha_envio: new Date().toISOString()
            })
            .eq('id_campana', campaignId);

        return {
            success: true,
            sent,
            failed
        };
    } catch (error: any) {
        Logger.error('Error sending campaign', error);

        // Update campaign status to "cancelada" on error
        await supabase
            .from('campanas_email')
            .update({ estado: 'cancelada' })
            .eq('id_campana', campaignId);

        return {
            success: false,
            sent: 0,
            failed: 0,
            error: error.message
        };
    }
};

/**
 * Get all campaigns
 */
export const getCampaigns = async (): Promise<any[]> => {
    try {
        const { data, error } = await supabase
            .from('campanas_email')
            .select('*')
            .order('fecha_creacion', { ascending: false });

        if (error) throw error;
        return data || [];
    } catch (error) {
        Logger.error('Error getting campaigns', error);
        return [];
    }
};

/**
 * Delete campaign
 */
export const deleteCampaign = async (campaignId: number): Promise<boolean> => {
    try {
        const { error } = await supabase
            .from('campanas_email')
            .delete()
            .eq('id_campana', campaignId);

        if (error) throw error;
        return true;
    } catch (error) {
        Logger.error('Error deleting campaign', error);
        return false;
    }
};

/**
 * Send chat transcript to user
 */
export const sendChatTranscript = async (
    email: string,
    transcript: { role: 'user' | 'assistant'; content: string }[],
    subject: string = 'Tu historial de chat con PublicaloGratis'
): Promise<EmailResponse> => {
    if (!email || !validateEmail(email)) {
        return { success: false, error: 'Invalid email address' };
    }

    // Format transcript as HTML
    const transcriptHtml = (transcript || []).map(msg => `
        <div style="margin-bottom: 15px; padding: 10px; border-radius: 8px; background-color: ${msg.role === 'user' ? '#eef2ff' : '#f3f4f6'};">
            <strong>${msg.role === 'user' ? 'Tú' : 'Asistente'}:</strong>
            <div style="white-space: pre-wrap; margin-top: 5px;">${msg.content.replace(/\n/g, '<br>')}</div>
        </div>
    `).join('');

    const html = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>🗪 Historial de Chat</h2>
            <p>Aquí tienes la copia de tu conversación reciente:</p>
            <div style="border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin: 20px 0;">
                ${transcriptHtml}
            </div>
            <p style="color: #6b7280; font-size: 12px;">Enviado automáticamente por PublicaloGratis.com</p>
        </div>
    `;

    return sendEmail({
        to: email,
        bcc: 'soporte@publicalogratis.com', // Send copy to support
        subject,
        html
    });
};

/**
 * Send order confirmation email to the CUSTOMER
 */
export const sendOrderConfirmationEmail = async (
    user: Usuario | { email: string; nombre: string },
    order: any, // Using any for flexibility with Pedido type
    tiendaName: string
): Promise<EmailResponse> => {
    if (!user.email || !validateEmail(user.email)) {
        return { success: false, error: 'Invalid email address' };
    }

    // Simple HTML table for items
    const itemsHtml = (order.items || []).map((item: any) => `
        <tr>
            <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.titulo}</td>
            <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.cantidad}</td>
            <td style="padding: 10px; border-bottom: 1px solid #eee;">$${item.precio}</td>
            <td style="padding: 10px; border-bottom: 1px solid #eee;">$${item.subtotal}</td>
        </tr>
    `).join('');

    const html = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>¡Tu pedido ha sido creado! 🎉</h2>
            <p>Hola <strong>${user.nombre}</strong>,</p>
            <p>Gracias por tu compra en <strong>${tiendaName}</strong>. Aquí tienes los detalles de tu pedido:</p>
            
            <div style="background-color: #f9fafb; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <p><strong>Pedido #:</strong> ${order.numero_pedido}</p>
                <p><strong>Total:</strong> $${order.total}</p>
                <p><strong>Estado:</strong> Pendiente de Pago/Coordinación</p>
            </div>

            <table style="width: 100%; border-collapse: collapse;">
                <thead>
                    <tr style="background-color: #f3f4f6; text-align: left;">
                        <th style="padding: 10px;">Producto</th>
                        <th style="padding: 10px;">Cant</th>
                        <th style="padding: 10px;">Precio</th>
                        <th style="padding: 10px;">Subtotal</th>
                    </tr>
                </thead>
                <tbody>
                    ${itemsHtml}
                </tbody>
            </table>

            <p style="margin-top: 20px;">
                La tienda se pondrá en contacto contigo pronto para coordinar el pago y el envío.
            </p>
        </div>
    `;

    return sendEmail({
        to: user.email,
        bcc: 'soporte@publicalogratis.com',
        subject: `✅ Pedido Confirmado: #${order.numero_pedido}`,
        html
    });
};

/**
 * Send new order notification to the STORE OWNER
 */
export const sendNewOrderNotificationToStore = async (
    storeEmail: string,
    storeName: string,
    customer: { nombre: string; email: string; telefono?: string },
    order: any
): Promise<EmailResponse> => {
    if (!storeEmail || !validateEmail(storeEmail)) {
        Logger.warn('Invalid store email for notification');
        return { success: false, error: 'Invalid store email' };
    }

    const itemsHtml = (order.items || []).map((item: any) => `
        <li>${item.cantidad}x <strong>${item.titulo}</strong> ($${item.subtotal})</li>
    `).join('');

    const html = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2>¡Nueva Venta en ${storeName}! 🤑</h2>
            <p>Has recibido un nuevo pedido a través del Asistente Virtual.</p>
            
            <div style="background-color: #f0fdf4; padding: 20px; border-radius: 8px; margin: 20px 0; border: 1px solid #bbf7d0;">
                <h3 style="margin-top:0;">Pedido #${order.numero_pedido}</h3>
                <p style="font-size: 1.25em; font-weight: bold;">Total: $${order.total}</p>
                <ul>
                    ${itemsHtml}
                </ul>
            </div>

            <h3>Datos del Cliente:</h3>
            <ul>
                <li><strong>Nombre:</strong> ${customer.nombre}</li>
                <li><strong>Email:</strong> ${customer.email}</li>
                <li><strong>Teléfono/WhatsApp:</strong> ${customer.telefono || 'No especificado'}</li>
            </ul>

            <p style="margin-top: 20px;">
                <a href="https://publicalogratis.com/mi-tienda/pedidos" style="background-color: #000; color: #fff; padding: 10px 20px; text-decoration: none; border-radius: 5px;">Gestionar Pedido</a>
            </p>
        </div>
    `;

    return sendEmail({
        to: storeEmail,
        bcc: 'soporte@publicalogratis.com',
        subject: `💰 Nueva Venta #${order.numero_pedido} - ${storeName}`,
        html
    });
};

/**
 * Send access request notification to Store Owner
 */
export const sendStoreAccessRequestEmail = async (
    ownerEmail: string,
    storeName: string,
    requester: { nombre: string; email: string; whatsapp?: string },
    message?: string
): Promise<EmailResponse> => {
    if (!ownerEmail || !validateEmail(ownerEmail)) return { success: false, error: 'Invalid owner email' };

    const html = emailTemplates.storeAccessRequest({
        storeName,
        requesterName: requester.nombre,
        requesterEmail: requester.email,
        whatsapp: requester.whatsapp,
        message
    });

    return sendEmail({
        to: ownerEmail,
        subject: `🔒 Solicitud de Acceso: ${requester.nombre} - ${storeName}`,
        html
    });
};

/**
 * Send access decision notification to Requester
 */
export const sendStoreAccessDecisionEmail = async (
    userEmail: string,
    userName: string,
    storeName: string,
    status: 'aprobado' | 'rechazado' | 'bloqueado'
): Promise<EmailResponse> => {
    if (!userEmail || !validateEmail(userEmail)) return { success: false, error: 'Invalid user email' };

    let html = '';
    let subject = '';

    if (status === 'aprobado') {
        html = emailTemplates.storeAccessApproved({
            requesterName: userName,
            storeName
        });
        subject = `✅ Acceso Aprobado a ${storeName}`;
    } else {
        html = emailTemplates.storeAccessRejected({
            requesterName: userName,
            storeName
        });
        subject = `Acceso Denegado a ${storeName}`;
    }

    return sendEmail({
        to: userEmail,
        subject,
        html
    });
};

/**
 * Send order status update email to Customer
 */
export const sendOrderStatusUpdateEmail = async (
    customerEmail: string,
    customerName: string,
    orderNumber: string,
    storeName: string,
    newStatus: string,
    total: number
): Promise<EmailResponse> => {
    if (!customerEmail || !validateEmail(customerEmail)) return { success: false, error: 'Invalid customer email' };

    const html = emailTemplates.orderStatusUpdate({
        userName: customerName,
        avisoTitulo: orderNumber, // reusing field for order number
        storeName,
        razon: newStatus, // reusing field for status
        monto: total
    });

    let subject = `Actualización de Pedido #${orderNumber}`;
    if (newStatus === 'confirmado') subject = `✅ Pedido #${orderNumber} Confirmado`;
    if (newStatus === 'enviado') subject = `🚚 Pedido #${orderNumber} Enviado`;
    if (newStatus === 'entregado') subject = `🎉 Pedido #${orderNumber} Entregado`;
    if (newStatus === 'cancelado') subject = `❌ Pedido #${orderNumber} Cancelado`;

    return sendEmail({
        to: customerEmail,
        subject,
        html
    });
};
